import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import {
  useTodasLasNotificaciones,
  useMarcarLeida,
  useMarcarTodasLeidas,
  useEliminarNotificacion,
  useEliminarTodas,
  type Notificacion,
  type TipoNotificacion,
} from '../hooks/useNotificaciones'
import {
  IconoReloj,
  IconoIncidencias,
  IconoAvisos,
  IconoCerrar,
  IconoQuitar,
  IconoVacio,
  IconoBuscar,
  IconoCampanaAlerta,
} from '../componentes/iconos'

/**
 * Centro de alertas (/alertas): espejo a pantalla completa de la campana, pensado
 * para cuando hay DECENAS de notificaciones y el desplegable se queda corto.
 *
 * El eje es el TIEMPO (lo que pidió el usuario: "por fecha y hora"): las alertas
 * se agrupan por día (Hoy · Ayer · fecha larga) con la hora a la derecha. Encima,
 * un buscador por cliente/texto y los filtros por tipo que ya existen en la
 * campana (Aplazamientos · Incidencias · Avisos), para no cambiar el modelo mental.
 *
 * El estado de filtro y búsqueda vive en la URL (?tipo=, ?q=) para ser
 * compartible y sobrevivir a recargas.
 */

/** Color + icono por tipo (idéntico a la campana para coherencia visual). */
const ESTILO: Record<
  TipoNotificacion,
  { etiqueta: string; Icono: typeof IconoReloj; clase: string }
> = {
  aplazamiento: { etiqueta: 'Aplazamiento', Icono: IconoReloj, clase: 'bg-aviso-fondo text-aviso-icono' },
  incidencia: { etiqueta: 'Incidencia', Icono: IconoIncidencias, clase: 'bg-negativo-fondo text-negativo-tinta' },
  aviso: { etiqueta: 'Aviso', Icono: IconoAvisos, clase: 'bg-positivo-fondo text-positivo-tinta' },
}

type FiltroTipo = 'todas' | TipoNotificacion

/** Solo la hora (HH:mm) para la columna derecha. */
function soloHora(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
}

/**
 * Etiqueta del grupo de fecha: "Hoy", "Ayer" o "lunes, 27 de octubre".
 * Devuelve también una clave estable (YYYY-MM-DD) para agrupar sin líos de zona.
 */
function grupoFecha(iso: string): { clave: string; etiqueta: string } {
  const d = new Date(iso)
  const clave = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const hoy = new Date()
  const ayer = new Date()
  ayer.setDate(hoy.getDate() - 1)
  const mismaFecha = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  let etiqueta: string
  if (mismaFecha(d, hoy)) etiqueta = 'Hoy'
  else if (mismaFecha(d, ayer)) etiqueta = 'Ayer'
  else {
    etiqueta = d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
    etiqueta = etiqueta.charAt(0).toUpperCase() + etiqueta.slice(1)
  }
  return { clave, etiqueta }
}

/** Quita acentos y pasa a minúsculas para buscar de forma tolerante. */
function normaliza(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

export function Alertas() {
  const [params, setParams] = useSearchParams()
  const navegar = useNavigate()

  const filtro = (params.get('tipo') as FiltroTipo | null) ?? 'todas'
  const busqueda = params.get('q') ?? ''

  const { data, isLoading } = useTodasLasNotificaciones()
  const marcarLeida = useMarcarLeida()
  const marcarTodas = useMarcarTodasLeidas()
  const eliminar = useEliminarNotificacion()
  const eliminarTodas = useEliminarTodas()

  const items = useMemo(() => data?.items ?? [], [data])

  // Contadores por tipo (para los chips), siempre sobre el total, no sobre el filtrado.
  const conteos = useMemo(() => {
    const c = { todas: items.length, aplazamiento: 0, incidencia: 0, aviso: 0 }
    for (const n of items) c[n.tipo] += 1
    return c
  }, [items])

  const noLeidas = useMemo(() => items.filter((n) => !n.leida).length, [items])

  // Aplica tipo + búsqueda.
  const filtradas = useMemo(() => {
    const q = normaliza(busqueda.trim())
    return items.filter((n) => {
      if (filtro !== 'todas' && n.tipo !== filtro) return false
      if (!q) return true
      return normaliza(`${n.titulo} ${n.cuerpo ?? ''}`).includes(q)
    })
  }, [items, filtro, busqueda])

  // Agrupa por día conservando el orden (ya vienen recientes primero del backend).
  const grupos = useMemo(() => {
    const mapa = new Map<string, { etiqueta: string; items: Notificacion[] }>()
    for (const n of filtradas) {
      const { clave, etiqueta } = grupoFecha(n.creadaEn)
      if (!mapa.has(clave)) mapa.set(clave, { etiqueta, items: [] })
      mapa.get(clave)!.items.push(n)
    }
    return [...mapa.values()]
  }, [filtradas])

  const cambiarTipo = (t: FiltroTipo) => {
    const sig = new URLSearchParams(params)
    if (t === 'todas') sig.delete('tipo')
    else sig.set('tipo', t)
    setParams(sig, { replace: true })
  }

  const cambiarBusqueda = (v: string) => {
    const sig = new URLSearchParams(params)
    if (v) sig.set('q', v)
    else sig.delete('q')
    setParams(sig, { replace: true })
  }

  const alPulsar = (n: Notificacion) => {
    if (!n.leida) marcarLeida.mutate(n.id)
    if (n.enlace) navegar(n.enlace)
  }

  const CHIPS: { id: FiltroTipo; etiqueta: string }[] = [
    { id: 'todas', etiqueta: 'Todas' },
    { id: 'aplazamiento', etiqueta: 'Aplazamientos' },
    { id: 'incidencia', etiqueta: 'Incidencias' },
    { id: 'aviso', etiqueta: 'Avisos' },
  ]

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-tinta">
            <IconoCampanaAlerta className="size-6 text-acento" />
            Centro de alertas
          </h1>
          <p className="mt-1 text-sm text-tinta-2">
            Todas las notificaciones por fecha y hora. {noLeidas > 0 ? `${noLeidas} sin leer.` : 'Todo al día.'}
          </p>
        </div>
        {items.length > 0 ? (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => marcarTodas.mutate()}
              disabled={noLeidas === 0}
              className="rounded-lg border border-borde px-3 py-2 text-sm font-semibold text-acento transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:opacity-40"
            >
              Marcar todas leídas
            </button>
            <button
              type="button"
              onClick={() => eliminarTodas.mutate()}
              className="flex items-center gap-2 rounded-lg border border-borde px-3 py-2 text-sm font-semibold text-tinta-2 transition-colors hover:border-negativo-tinta hover:text-negativo-tinta"
            >
              <IconoQuitar className="size-4" />
              Vaciar
            </button>
          </div>
        ) : null}
      </div>

      {/* Filtros + buscador */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {CHIPS.map(({ id, etiqueta }) => {
            const activo = filtro === id
            return (
              <button
                key={id}
                type="button"
                onClick={() => cambiarTipo(id)}
                className={[
                  'flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors',
                  activo
                    ? 'border-acento bg-acento-suave text-acento-tinta'
                    : 'border-borde bg-superficie text-tinta-2 hover:bg-elevado',
                ].join(' ')}
              >
                {etiqueta}
                <span
                  className={[
                    'rounded-full px-2 py-px text-xs',
                    activo ? 'bg-acento text-white' : 'bg-elevado text-tinta-2',
                  ].join(' ')}
                >
                  {conteos[id]}
                </span>
              </button>
            )
          })}
        </div>
        <div className="relative ml-auto min-w-[220px] flex-1 md:max-w-xs">
          <IconoBuscar className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-apagada" />
          <input
            type="search"
            value={busqueda}
            onChange={(e) => cambiarBusqueda(e.target.value)}
            placeholder="Buscar cliente o texto…"
            className="w-full rounded-lg border border-borde bg-superficie py-2 pl-9 pr-3 text-sm text-tinta placeholder:text-tinta-apagada focus:border-acento focus:outline-none"
          />
        </div>
      </div>

      {/* Timeline */}
      {isLoading ? (
        <p className="py-10 text-center text-sm text-tinta-2">Cargando alertas…</p>
      ) : filtradas.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-borde bg-superficie px-4 py-16 text-center">
          <IconoVacio className="size-10 text-tinta-apagada" />
          <p className="text-sm text-tinta-2">
            {items.length === 0 ? 'No hay alertas' : 'Ninguna alerta coincide con el filtro'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {grupos.map((g) => (
            <div key={g.etiqueta}>
              <div className="mb-2 flex items-center gap-3">
                <h2 className="text-xs font-bold uppercase tracking-wider text-tinta-apagada">{g.etiqueta}</h2>
                <span className="h-px flex-1 bg-borde" />
                <span className="text-xs text-tinta-apagada">{g.items.length}</span>
              </div>
              <div className="overflow-hidden rounded-2xl border border-borde bg-superficie">
                {g.items.map((n) => {
                  const { etiqueta, Icono, clase } = ESTILO[n.tipo]
                  return (
                    <div
                      key={n.id}
                      className={[
                        'group relative flex gap-3 border-b border-borde px-4 py-3 last:border-b-0 transition-colors',
                        n.leida ? '' : 'bg-acento-suave',
                      ].join(' ')}
                    >
                      <span
                        className={['mt-1.5 size-2 shrink-0 rounded-full', n.leida ? 'bg-transparent' : 'bg-acento'].join(' ')}
                        aria-hidden
                      />
                      <div className={['flex size-9 shrink-0 items-center justify-center rounded-lg', clase].join(' ')}>
                        <Icono className="size-5" />
                      </div>
                      <button
                        type="button"
                        onClick={() => alPulsar(n)}
                        className={['min-w-0 flex-1 text-left', n.enlace ? 'cursor-pointer' : 'cursor-default'].join(' ')}
                      >
                        <span
                          className={['mb-0.5 inline-block rounded px-1.5 py-px text-[10px] font-bold uppercase tracking-wide', clase].join(' ')}
                        >
                          {etiqueta}
                        </span>
                        <p className="text-sm font-semibold text-tinta">{n.titulo}</p>
                        {n.cuerpo ? <p className="mt-0.5 text-xs text-tinta-2">{n.cuerpo}</p> : null}
                      </button>
                      <div className="flex shrink-0 flex-col items-end gap-2">
                        <span className="text-xs text-tinta-apagada">{soloHora(n.creadaEn)}</span>
                        <button
                          type="button"
                          onClick={() => eliminar.mutate(n.id)}
                          aria-label="Descartar"
                          className="rounded p-1 text-tinta-apagada opacity-0 transition-opacity hover:text-negativo-tinta group-hover:opacity-100"
                        >
                          <IconoCerrar className="size-4" />
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
